// Define our variable passed via an inline script here so we don't get problems later on.
// Current blog ID is shared across functions, so set that here and give a fallback.
const importedQalaPriceListData = QalaPriceListData ?? {}; // eslint-disable-line
const currentBlogID = Object.hasOwn( importedQalaPriceListData, 'blogID' ) ? importedQalaPriceListData.blogId : '1';

const getPriceListData = async ( productIDs, productTypes, priceListName, uid = 'sku' ) => {
	// Return the prices for the incoming unique identifiers.
	// Use the API URL.
	// Use the API endpoint.
	const apiEndpoint = 'qala-price-list/v1/price-list';
	const apiQuery = new URLSearchParams( {
		priceList: priceListName,
		product_uids: productIDs,
		product_types: productTypes,
		uniqueIdentifier: uid,
	} );
	const apiURL = 'https://hacotellus.se.dev.synot.io/wp-json/' + apiEndpoint + '?' + apiQuery.toString();

	try {
		const response = await fetch( apiURL );

		if ( ! response.ok ) {
			throw new Error( `Response status: ${ response.status }` );
		}

		return await response.json();
	} catch ( error ) {
		console.error( error.message ); // eslint-disable-line
	}

	return [];
};

const combinePriceListData = ( existingPriceData, priceListData, uniqueIdentifier ) => {
	// Exit early if priceListData is empty, doesn't have a 'products' key or the 'products' array is empty.
	if (
		priceListData.length === 0
	) {
		return existingPriceData;
	}

	const uniqueIdentifierKey = uniqueIdentifier === 'product_id' ? 'id' : 'sku';

	// For each existingPriceData, append the appropriate priceListData based on the unique identifier.
	const modifiedData = existingPriceData.map(
		existingData => {
			// Does the product have the unique identifier?
			if ( Object.hasOwn( existingData, uniqueIdentifierKey ) ) {
				// Look up a match in the price list data.
				const filteredPriceListItem = priceListData.find( priceListItem => {
					if ( uniqueIdentifier === 'product_id' ) {
						return priceListItem.uid === existingData[ uniqueIdentifierKey ].replaceAll( `${ currentBlogID }_`, '' );
					}

					return priceListItem.uid === existingData[ uniqueIdentifierKey ];
				} );

				// Set the prices if we matched our unique identifier.
				if ( typeof ( filteredPriceListItem ) !== 'undefined' ) {
					existingData.price = parseFloat( filteredPriceListItem.price );
					existingData.price_html = filteredPriceListItem.price_html;
				}
			}

			return existingData;
		}
	);

	return modifiedData;
};

const replacePriceWithPriceListValue = ( modifiedPrices, uniqueIdentifier ) => {
	const productElSelector = '.qec-results__product.qec-modern-product';
	const productElements = document.querySelectorAll( productElSelector );

	productElements.forEach( productElement => {
		const productSkuSelector = '.qec-modern-product__sku';
		const productID = productElement.dataset.id ? productElement.dataset.id.replaceAll( `${ currentBlogID }_`, '' ) : '';
		const identifierEl = productElement.querySelector( productSkuSelector );

		if ( typeof ( identifierEl ) === 'undefined' || identifierEl === null ) {
			return;
		}

		const identifierKey = uniqueIdentifier === 'product_id' ? 'id' : uniqueIdentifier;
		const identifierText = uniqueIdentifier === 'sku' ? identifierEl.innerText : productID;

		// Look up the SKU in our modifiedPrices list.
		const productData = modifiedPrices.filter( modifiedPrice => {
			if ( uniqueIdentifier === 'product_id' ) {
				return identifierText === modifiedPrice[ identifierKey ].replaceAll( `${ currentBlogID }_`, '' );
			}

			return identifierText === modifiedPrice[ identifierKey ];
		} );

		// Try and exit anywhere that might fail.
		if (
			productData.length === 0 ||
			typeof ( productData[ 0 ] ) === 'undefined' ||
			Object.hasOwn( productData[ 0 ], 'price_html' ) === false
		) {
			return;
		}

		const priceElSelector = productData[ 0 ].product_type === 'variable' ? '.qec-modern-product__price.price > .price-range' : '.qec-modern-product__price.price';
		const priceElement = productElement.querySelector( priceElSelector );

		if ( typeof ( priceElement ) === 'undefined' || priceElement === null ) {
			return;
		}

		// Setting innerHTML can cause injection issues.
		// Converting price_html to a node and replacing the current contents is a bit safer.
		// We also add a wrapper that is immediately removed by 'firstElementChild'. This allows us to return multiple elements (e.g. price ranges).
		const parser = new window.DOMParser();
		const newPriceEl = parser.parseFromString( '<span>' + productData[ 0 ].price_html + '</span>', 'text/html' ).body.firstElementChild;
		priceElement.replaceChildren( newPriceEl );
	} );
};

export const detectPriceListPrices = async results => {
	const uniqueIdentifier = importedQalaPriceListData.uniqueIdentifier ?? 'sku';

	// Exit if there are no search results.
	// Is the user logged in? Exit if not.
	// Is the logged in user attached to a price list? Exit if not.
	if (
		results.length === 0 ||
		'loggedIn' in importedQalaPriceListData === false ||
		importedQalaPriceListData.loggedIn === false ||
		'userPriceList' in importedQalaPriceListData === false ||
		importedQalaPriceListData.userPriceList === null ||
		importedQalaPriceListData.userPriceList === ''
	) {
		return;
	}

	// User is logged in and attached to a price list. Let's do some work.

	// Reduce our results list into an array of SKUs or 'null'.
	// This means we maintain the position of items, even if they don't have a SKU.
	const currentSKUs = results.map( result => 'sku' in result ? result.sku : null );
	const currentIDs = results.map( result => 'id' in result ? result.id.replaceAll( `${ currentBlogID }_`, '' ) : null );
	const currentTypes = results.map( result => result.product_type ?? 'simple' );

	// Hit the API to get the prices for the given SKUs on the current price list.
	const priceListPrices = await getPriceListData(
		uniqueIdentifier === 'sku' ? currentSKUs : currentIDs,
		currentTypes,
		importedQalaPriceListData.userPriceList,
		uniqueIdentifier
	);

	if (
		priceListPrices.length === 0 ||
		Object.hasOwn( priceListPrices, 'products' ) === false ||
		priceListPrices.products.length === 0
	) {
		return;
	}

	// Merge the incoming prices with default prices.
	const mergedPrices = combinePriceListData( results, priceListPrices.products, uniqueIdentifier );

	// Attach the correct price to the correct product.
	replacePriceWithPriceListValue( mergedPrices, uniqueIdentifier );
};
